/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useCallback, useContext, useEffect, useState} from 'react';
import {ResponseData} from '@dvrd/dvr-controls';
import {AppContext} from '../../misc/appContext';
import Account from '../../../models/accountModel';
import {FilenameBase, InvoiceSettingsErrors} from '../../../util/interfaces';
import {sendGaEvent, sendGaPageView, trackPlatformAction} from '../../../util/analytics';
import {onSetCrumbs} from '../../../util/events';
import InvoiceSettings from './invoiceSettings';
import EmailTemplate from '../../../models/emailTemplateModel';
import {getCreditEmailStep, updateCreditSettings} from '../../../util/requestSender';
import EmailStep from '../../../models/emailStepModel';
import {createErrorMessage} from '../../../util/utils';
import {onShowConfirmSnackbar} from '../../../util/renderUtil';
import {setNavigationParam} from '../../../util/navigateUtil';
import {useNavigate} from 'react-router';
import {SettingsFields} from '../../../models/settingsModel';
import PageChangePermission from '../../misc/pageChangePermission';
import {PermissionCallback} from '../../../util/pageChanger';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const EMPTY_ERRORS: InvoiceSettingsErrors = {
    invoicePrefix: null,
    invoiceStart: null,
    oldInvoiceStart: null,
    oldInvoicePrefix: null
};

export default function InvoiceSettingsController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const [account, setAccount] = useState(context.accountContext.account ?? new Account({}));
    const numbersDoDiffer = useCallback(() => {
        const settings = account.settings;
        if (!settings) return false;
        return (
            settings.invoicePrefix !== settings.creditInvoiceNumPrefix ||
            settings.invoiceStart !== settings.creditInvoiceNumStart
        );
    }, [account]);

    const [errors, setErrors] = useState(EMPTY_ERRORS);
    const [loading, setLoading] = useState(false);
    const [changed, setChanged] = useState(false);
    const [invoicePreview, setInvoicePreview] = useState(account.settings.invoiceStart);
    const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
    const [creditEmailStep, setEmailStep] = useState<EmailStep>(new EmailStep({}));
    const [differNumbers, setDifferNumbers] = useState(numbersDoDiffer());
    const navigate = useNavigate();

    function onChangeFilenameBase(value: FilenameBase) {
        return function () {
            const _account = account.clone();
            _account.settings.filenameBase = value;
            setChanged(true);
            setAccount(_account);
        };
    }

    function onChangeDiffers(differs: boolean) {
        setDifferNumbers(differs);
        if (!differs) {
            const _account = account.clone();
            _account.settings.creditInvoiceNumPrefix = _account.settings.invoicePrefix;
            _account.settings.creditInvoiceNumStart = _account.settings.invoiceStart;
            setChanged(true);
            setAccount(_account);
        }
    }

    function onChangeSetting(key: keyof SettingsFields) {
        return function (value: string) {
            const acc = account.clone();
            acc.settings.setField(key, value);
            setChanged(true);
            if (key === 'invoice_start') setInvoicePreview(value);
            setAccount(acc);
        };
    }

    function onChangeCheck(key: keyof SettingsFields) {
        return function (value: boolean) {
            const _account = account.clone();
            _account.settings.setField(key, value);
            if (key === 'auto_create_credit_invoices' && !value) _account.settings.autoSendCreditInvoices = false;
            setChanged(true);
            setAccount(_account);
        };
    }

    function onChangeInvoicePreview(value: string) {
        setInvoicePreview(value);
    }

    function onResetError(key: keyof InvoiceSettingsErrors) {
        return function () {
            setErrors(Object.assign({}, errors, {[key]: null}));
        };
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        if (changed)
            onSubmit(() => {
                callback(id, true);
            })();
        else callback(id, true);
    }

    function onSubmit(callback?: VoidFunction) {
        return function () {
            sendGaEvent('Click', 'Factuur instellingen', 'Opslaan');
            if (!validateValues()) return;
            setLoading(true);
            const copy = account.clone();
            account.update(
                undefined,
                undefined,
                undefined,
                (_account: Account, success: boolean, data: ResponseData) => {
                    if (success) {
                        onSubmitCredit(copy, callback);
                    } else {
                        showDialog(
                            data.message ||
                                t(
                                    'settings.invoice.saveFailedMsg',
                                    'Het opslaan van de factuurinstellingen is niet gelukt.'
                                ),
                            t('settings.invoice.saveFailedTitle', 'Opslaan mislukt')
                        );
                        setLoading(false);
                    }
                }
            );
        };
    }

    function onSubmitCredit(account: Account, callback?: VoidFunction) {
        const sett = account.settings;
        setLoading(true);
        updateCreditSettings({
            data: {
                account_id: account.id,
                auto_create_credit_invoices: sett.autoCreateCreditInvoices,
                auto_send_credit_invoices: sett.autoSendCreditInvoices,
                credit_invoice_num_prefix: sett.creditInvoiceNumPrefix,
                credit_invoice_num_start: sett.creditInvoiceNumStart
            },
            callback: (data: ResponseData) => {
                if (data.success) {
                    trackPlatformAction('settings.invoices', 'save_settings');
                    onShowConfirmSnackbar(t('settings.invoice.settingsSaved', 'De instellingen zijn opgeslagen'));
                    const account = new Account(data.account);
                    onAccountUpdated(account);
                    setChanged(false);
                    callback?.();
                    if (account.settings.autoSendCreditInvoices)
                        setEmailStep(creditEmailStep.clone().setField('active', true));
                } else {
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'settings.invoice.updateFailedMsg',
                                    'Het bijwerken van de instellingen is niet gelukt.'
                                )
                        ),
                        t('settings.invoice.updateFailedTitle', 'Bijwerken mislukt')
                    );
                    setLoading(false);
                }
            }
        });
    }

    function onAccountUpdated(account: Account, callback?: VoidFunction) {
        const accounts = context.accountContext.accounts.slice();
        for (let i = 0; i < accounts.length; i++) {
            if (accounts[i].id === account.id) {
                accounts.splice(i, 1, account);
                break;
            }
        }
        context.onChangeContext({account, accounts}, callback);
    }

    function onNavigateTemplate(template: EmailTemplate, step: EmailStep) {
        setNavigationParam({template, step, returnPage: '/instellingen/factuur-instellingen'});
        navigate('/template-editor');
    }

    function onReplaceCreditEmail(step: EmailStep) {
        setEmailStep(step);
    }

    function validateValues(): boolean {
        let valid = true;
        const {settings} = account,
            errs = Object.assign({}, errors);
        if (!settings.invoicePrefix.length) {
            errs.invoicePrefix = t('settings.invoice.errInvalid', 'Niet geldig');
            valid = false;
        }
        if (!settings.invoiceStart.length) {
            errs.invoiceStart = t('settings.invoice.errInvalid', 'Niet geldig');
            valid = false;
        }
        if (!valid) setErrors(errs);
        return valid;
    }

    function getEmailStep() {
        const {account} = context.accountContext;
        if (!account) return;
        setLoading(true);
        getCreditEmailStep({
            account_id: account.id,
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) setEmailStep(new EmailStep(data.email_step));
                else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('settings.invoice.loadFailedMsg', 'Het laden van de instellingen is niet gelukt.')
                        ),
                        t('settings.invoice.loadFailedTitle', 'Laden mislukt')
                    );
            }
        });
    }

    function getEmailTemplates() {
        const {account} = context.accountContext;
        if (!account) return;
        EmailTemplate.getAll(account.id, -1, -1, (templates: EmailTemplate[]) => {
            setEmailTemplates(templates);
        });
    }

    useEffect(() => {
        sendGaPageView('/instellingen/factuur');
        onSetCrumbs([
            {label: t('settings.invoice.crumbSettings', 'Instellingen')},
            {label: t('settings.invoice.crumbInvoices', 'Facturen')}
        ]);
    }, []);

    useEffect(() => {
        const {account} = context.accountContext;
        if (account) {
            setAccount(account);
            setInvoicePreview(account.settings.invoiceStart);
            setErrors(EMPTY_ERRORS);
            getEmailTemplates();
            getEmailStep();
        }
    }, [context.accountContext.account]);

    return (
        <>
            <InvoiceSettings
                onChangeSetting={onChangeSetting}
                onResetError={onResetError}
                onSubmit={onSubmit()}
                onChangeInvoicePreview={onChangeInvoicePreview}
                settings={account.settings}
                errors={errors}
                loading={loading}
                hasChanged={changed}
                invoicePreview={invoicePreview}
                onChangeCheck={onChangeCheck}
                creditEmailStep={creditEmailStep}
                emailTemplates={emailTemplates}
                onReplaceCreditEmail={onReplaceCreditEmail}
                differNumbers={differNumbers}
                onChangeDifferNumbers={onChangeDiffers}
                onChangeFilenameBase={onChangeFilenameBase}
                onNavigateTemplate={onNavigateTemplate}
            />
            <PageChangePermission
                changed={changed}
                onDeny={onDenyPageChange}
                denyText={t('settings.invoice.saveChanges', 'Ja, wijzigingen opslaan')}
                allowText={t('settings.invoice.leavePage', 'Nee, pagina verlaten')}
                allowSecondary={true}
            >
                {t(
                    'settings.invoice.unsavedChanges',
                    'De wijzigingen gaan verloren als je de pagina verlaat. Wil je de wijzigingen eerst opslaan?'
                )}
            </PageChangePermission>
        </>
    );
}
